/**
 * Mehrere Zeiträume auf einmal an den Kanalmanager übertragen.
 *
 * Jede Zeile ist ein eigenständiger Zeitraum; abgeschickt wird alles in einem Aufruf, um das
 * Anfragelimit des Vendors zu schonen. Höchstens 50 Zeiträume (Serverprüfung
 * `MaxRangesPerBatch`).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import type { AriPushBatchRange } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
} from '@/shared/components';
import { MultiRangeEditor } from './MultiRangeEditor';
import { AriOwnershipImpactDialog } from './AriOwnershipImpactDialog';
import { useAriPushBatch } from './useAriPushBatch';
import { isMultiRangeRowValid, rowHasAnyValue, toBackendRange } from './multiRangeRowModel';
import { MULTI_RANGE_PRESETS, presetById } from './multiRangePresets';
import { useMultiRangeRows } from './useMultiRangeRows';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  apartmentName?: string;
  initialRange?: { from: string; to: string };
}

export function MultiRangePushModal({
  open,
  onOpenChange,
  propertyId,
  apartmentName,
  initialRange,
}: Props) {
  const { rows, patchRow, addRow, removeRow, replaceRows } = useMultiRangeRows(open, initialRange);
  const [confirmation, setConfirmation] = useState<AriPushBatchRange[] | null>(null);
  const push = useAriPushBatch({
    onSuccess: () => {
      setConfirmation(null);
      onOpenChange(false);
    },
  });

  function submit() {
    const usable = rows.filter(rowHasAnyValue);
    if (usable.length === 0) {
      toast.error('Mindestens ein Zeitraum braucht einen Wert.');
      return;
    }
    if (usable.some((r) => !isMultiRangeRowValid(r))) {
      toast.error('Bitte korrigieren Sie die Zeiträume: das Ende liegt vor dem Beginn.');
      return;
    }
    setConfirmation(usable.map(toBackendRange));
  }

  const usableCount = rows.filter(rowHasAnyValue).length;

  return (
    <>
      <Dialog
        open={open && confirmation === null}
        onOpenChange={(value) => {
          if (!value) setConfirmation(null);
          if (!push.isPending) onOpenChange(value);
        }}
      >
        <DialogContent className="max-w-6xl" showClose={!push.isPending}>
          <DialogHeader>
            <DialogTitle>
              Mehrere Zeiträume{apartmentName ? ` für ${apartmentName}` : ''} übertragen
            </DialogTitle>
            <p className="text-ink-soft mt-1 text-xs">
              Alle Zeiträume gehen in einem Zug an den Kanalmanager. Leere Felder bleiben
              unverändert.
            </p>
          </DialogHeader>
          <DialogBody>
            <div className="border-line/40 bg-card-alt/30 mb-4 flex flex-wrap items-center gap-2 rounded border px-3 py-2 text-xs">
              <label
                className="text-ink-soft uppercase tracking-wider"
                htmlFor="multi-range-preset"
              >
                Vorlage einfügen
              </label>
              <Select
                id="multi-range-preset"
                value=""
                onChange={(e) => {
                  const p = presetById(e.target.value);
                  if (p) {
                    replaceRows(p.build());
                    toast.success(`Vorlage „${p.label}" eingefügt`);
                  }
                  e.target.value = '';
                }}
                className="py-1.5! text-xs"
              >
                <option value="">— Auswählen —</option>
                {MULTI_RANGE_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </Select>
              <span className="text-ink-soft sm:ml-auto">
                Ersetzt die aktuellen Zeilen; danach lassen sie sich weiter bearbeiten.
              </span>
            </div>

            <MultiRangeEditor
              rows={rows}
              disabled={push.isPending}
              onPatch={patchRow}
              onRemove={removeRow}
              onAdd={addRow}
            />
          </DialogBody>
          <DialogFooter className="flex-wrap justify-between gap-2">
            <span className="text-ink-soft text-xs">
              Ergänzt den Kalender. Eine vollständige Neuübertragung läuft über „Alles neu
              übertragen".
            </span>
            <span className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={push.isPending}>
                Abbrechen
              </Button>
              <Button variant="brand" onClick={submit} loading={push.isPending}>
                {usableCount === 1
                  ? '1 Zeitraum übertragen'
                  : `${usableCount} Zeiträume übertragen`}
              </Button>
            </span>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AriOwnershipImpactDialog
        open={open && confirmation !== null}
        propertyId={propertyId}
        ranges={confirmation ?? []}
        saving={push.isPending}
        onOpenChange={(value) => !value && setConfirmation(null)}
        onConfirm={() => confirmation && push.mutate({ propertyId, ranges: confirmation })}
      />
    </>
  );
}
